import React, { useState, useEffect } from "react";
import { Menu, X, FileText, Activity, LogOut } from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import Logo from "./components/Logo";
import AuthWrapper from "./components/AuthWrapper";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("BVN Count");
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutTimer, setLogoutTimer] = useState(10);
  const navigate = useNavigate();

  const navItems = [
    { name: "BVN Count", icon: Activity, path: "/dashboard/bvn_count" },
    { name: "BVN Logs", icon: FileText, path: "/dashboard/bvn_logs" },
    { name: "Logout", icon: LogOut },
  ];

  const handleNavClick = (navName) => {
    setActiveNav(navName);
    setSidebarOpen(false);

    if (navName === "Logout") {
      setShowLogoutDialog(true);
    }
  };

  useEffect(() => {
    let timer;
    if (showLogoutDialog && logoutTimer > 0) {
      timer = setInterval(() => {
        setLogoutTimer((prev) => prev - 1);
      }, 1000);
    } else if (showLogoutDialog && logoutTimer === 0) {
      localStorage.clear();
      navigate("/login");
    }
    return () => clearInterval(timer);
  }, [showLogoutDialog, logoutTimer, navigate]);

  const handleCancelLogout = () => {
    setShowLogoutDialog(false);
    setLogoutTimer(10);
  };

  const handleConfirmLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <AuthWrapper>
      <div className="min-h-screen bg-white flex">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white shadow-md transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:fixed lg:translate-x-0 lg:h-screen
      `}
        >
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div>
              <div className="flex items-center">
                <Logo />
                <h1 className="text-2xl font-bold text-gray-800">BVN Portal</h1>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-col h-[calc(100vh-120px)]">
            {/* Navigation items */}
            <nav className="flex-1 p-6">
              <div className="space-y-4">
                {navItems.slice(0, -1).map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.path}
                      onClick={() => handleNavClick(item.name)}
                      className={({ isActive }) =>
                        `w-full flex items-center space-x-4 px-6 py-4 rounded-lg text-white transition-all duration-200 font-medium transform hover:scale-105
                      ${
                        isActive
                          ? "bg-red-600 shadow-lg"
                          : "bg-red-500 hover:bg-red-600 hover:shadow-md"
                      }`
                      }
                    >
                      <Icon className="w-6 h-6" />
                      <span>{item.name}</span>
                    </NavLink>
                  );
                })}
              </div>
            </nav>

            <div className="p-6 pt-0">
              <button
                onClick={() => handleNavClick("Logout")}
                className="w-full flex items-center space-x-4 px-6 py-4 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all duration-200 font-medium transform hover:scale-105 hover:shadow-md"
              >
                <LogOut className="w-6 h-6" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-screen lg:ml-72">
          {/* Header */}
          <header className="bg-white shadow-sm border-b border-gray-100 px-6 py-[25.2px] sticky top-0 z-30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                >
                  <Menu className="w-6 h-6" />
                </button>

                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {activeNav}
                  </h2>
                </div>
              </div>

              <div className="text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                Welcome
              </div>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-y-auto">
            <Outlet />
          </main>
        </div>

        {/* Logout Confirmation Dialog */}
        {showLogoutDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xl font-bold text-red-600">
                    {logoutTimer}
                  </span>
                </div>
                <p className="text-lg font-bold text-red-600 text-center">
                  Are you sure you want to logout?
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={handleCancelLogout}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmLogout}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthWrapper>
  );
};

export default DashboardLayout;
